import { AdminHostPoolMenuContent } from "@/admin/AdminHostPoolSelect.tsx";
import { AdminMultiSelect, type MultiSelectItem } from "@/admin/AdminMultiSelect.tsx";
import { INPUT_DEBOUNCE_MS } from "@/admin/debounce.ts";
import type { GqFilterValue } from "@/admin/FieldConfig.tsx";
import { useFilterContext } from "@/admin/FilterContext.ts";
import { removeEmptyFilter } from "@/admin/filterUtils.ts";
import { AInput } from "@/admin/form/Fields.tsx";
import { ColumnFilterInputType } from "@/admin/graphql.gen/graphql.ts";
import { useAllHostPoolsQuery } from "@/admin/host_pools/hostPoolsQuery.ts";
import { defaultTz } from "@/admin/utils/time.ts";
import { WDatePicker } from "@/admin/WDatePicker.tsx";
import { WSelect } from "@/components/forms/WSelect.tsx";
import { fromAbsolute, toCalendarDate, toTimeZone, type CalendarDate } from "@internationalized/date";
import type { ReactNode } from "react";
import { TextField, type InputProps, type TextFieldProps } from "react-aria-components";
import { useDebounceCallback } from "usehooks-ts";

interface TextFilterProps {
    exact?: boolean;
}

export function TextFilter({ exact = false }: TextFilterProps) {
    const { value, setValue } = useFilterContext();
    const setValueDebounce = useDebounceCallback(setValue, INPUT_DEBOUNCE_MS);

    const type = exact ? ColumnFilterInputType.Exact : undefined;

    return (
        <TextField
            autoFocus
            className="w-8em"
            aria-label="Search text"
            defaultValue={value?.value ?? ""}
            onChange={(value) => setValueDebounce(value ? { type, value } : null)}
        >
            <AInput />
        </TextField>
    );
}

/** Debounced number form field component (for usage in filters) */
function FilterNumberField({
    defaultValue,
    onChange,
    children,
    placeholder,
    ...props
}: {
    defaultValue: number | null;
    onChange: (value: number | null) => void;
    children?: ReactNode;
} & Pick<TextFieldProps, "autoFocus"> &
    Pick<InputProps, "placeholder">) {
    const onChangeDebounce = useDebounceCallback(onChange, INPUT_DEBOUNCE_MS);

    return (
        <TextField
            aria-label={placeholder}
            className="HStack gap-1"
            pattern="-?[0-9]*\.?[0-9]*"
            inputMode="decimal"
            defaultValue={defaultValue != null ? String(defaultValue) : ""}
            onChange={(value) => {
                const num = value.trim() !== "" ? Number(value) : null;
                if (Number.isNaN(num)) {
                    return;
                }
                onChangeDebounce(num);
            }}
            {...props}
        >
            {children}
            <div
                // XXX: Have to use a wrapper div since `Input` styles force `width: 100%`
                className="w-8em"
            >
                <AInput placeholder={placeholder} />
            </div>
        </TextField>
    );
}

export function ExactNumberFilter() {
    const { value, setValue } = useFilterContext();
    return (
        <FilterNumberField
            autoFocus
            aria-label="Number"
            defaultValue={value?.value != null ? Number(value.value) : null}
            onChange={(value) =>
                setValue(value != null ? { type: ColumnFilterInputType.Exact, value: String(value) } : null)
            }
        />
    );
}

/** Convert a {@link CalendarDate} to a GraphQL filter {@link GqFilterValue.value}. */
function dateToFilterValue(date: CalendarDate | null): string | undefined {
    if (date == null) {
        return undefined;
    }
    return String(date.toDate(defaultTz).valueOf() / 1000);
}

/**
 * Convert a GraphQL filter {@link GqFilterValue.value} to a {@link CalendarDate}.
 *
 * @returns `null` when the filter is not defined
 */
function filterValueToDate(value: string | undefined): CalendarDate | null {
    if (value == null) {
        return null;
    }
    const time = fromAbsolute(Number(value) * 1000, "UTC");
    return toCalendarDate(toTimeZone(time, defaultTz));
}

export function UnixDateRangeFilter() {
    const { value, setValue } = useFilterContext();

    const dateValueFrom = filterValueToDate(value?.value);
    const dateValueTo = filterValueToDate(value?.value2);

    function updateValue(update: Partial<GqFilterValue>) {
        const newValue = { ...value, type: ColumnFilterInputType.Range, ...update };
        setValue(removeEmptyFilter(newValue));
    }

    return (
        <>
            <WDatePicker
                autoFocus
                placeholder="Start"
                defaultValue={dateValueFrom}
                onChange={(date) => {
                    updateValue({ value: dateToFilterValue(date) });
                }}
            />
            <ToLabel />
            <WDatePicker
                placeholder="End"
                defaultValue={dateValueTo}
                onChange={(date) => {
                    updateValue({ value2: dateToFilterValue(date) });
                }}
            />
        </>
    );
}

function ToLabel() {
    return <span className="text-muted">&mdash;</span>;
}

export function NumberRangeFilter() {
    const { value, setValue } = useFilterContext();

    const valueFrom = value?.value != null ? Number(value.value) : null;
    const valueTo = value?.value2 != null ? Number(value.value2) : null;

    function updateValue(update: Partial<GqFilterValue>) {
        const newValue = { ...value, type: ColumnFilterInputType.Range, ...update };
        setValue(removeEmptyFilter(newValue));
    }

    return (
        <>
            <FilterNumberField
                autoFocus
                placeholder="Min"
                defaultValue={valueFrom}
                onChange={(value) => {
                    updateValue({ value: value != null ? String(value) : undefined });
                }}
            />
            <ToLabel />
            <FilterNumberField
                placeholder="Max"
                defaultValue={valueTo}
                onChange={(value) => {
                    updateValue({ value2: value != null ? String(value) : undefined });
                }}
            />
        </>
    );
}

interface EnumItem {
    value: string;
    label: ReactNode;
}

const nullKey = "__null__";

interface EnumFilterProps {
    items: EnumItem[];
    mode?: "single" | "multiple" | "csv";
}

export function EnumFilter({ items, mode = "single" }: EnumFilterProps) {
    const { value, setValue } = useFilterContext();

    return mode === "single" ? (
        <WSelect
            autoFocus
            aria-label="Filter value"
            items={[{ value: null, label: <i>No filter</i> }, ...items]}
            getKey={(item) => item.value ?? nullKey}
            valueKey={value?.value ?? nullKey}
            onChange={({ value }) => {
                setValue(value ? { type: undefined, value } : null);
            }}
        >
            {(item) => item.label}
        </WSelect>
    ) : (
        <AdminMultiSelect
            value={(mode === "csv" ? value.value?.split(",") : value.values) ?? []}
            items={items.map((item): MultiSelectItem => ({ id: item.value, title: item.label }))}
            onChange={(value) => {
                setValue(
                    value.length > 0
                        ? mode === "csv"
                            ? { type: undefined, value: value.join(",") }
                            : { type: ColumnFilterInputType.List, values: value }
                        : null,
                );
            }}
        />
    );
}

interface EnumComponentFilterProps<T extends string> extends Omit<EnumFilterProps, "items"> {
    values: T[];
    component: React.FC<{
        value: T;
    }>;
}

export function EnumComponentFilter<T extends string>({
    values,
    component: Component,
    ...props
}: EnumComponentFilterProps<T>) {
    return <EnumFilter items={values.map((value) => ({ value, label: <Component value={value} /> }))} {...props} />;
}

export function DeletedFilter() {
    return (
        <EnumFilter
            items={[
                { value: "include_deleted", label: "Include deleted" },
                { value: "exclude_deleted", label: "Exclude deleted" },
            ]}
        />
    );
}

export function BooleanFilter({
    trueLabel = "Yes",
    falseLabel = "No",
}: {
    trueLabel?: ReactNode;
    falseLabel?: ReactNode;
}) {
    return (
        <EnumFilter
            items={[
                { value: "true", label: trueLabel },
                { value: "false", label: falseLabel },
            ]}
        />
    );
}

export function BooleanComponentFilter({ component: Component }: { component: React.FC<{ value: boolean }> }) {
    return <BooleanFilter trueLabel={<Component value={true} />} falseLabel={<Component value={false} />} />;
}

export function EnabledFilter() {
    return <BooleanFilter trueLabel="Enabled" falseLabel="Disabled" />;
}

export function HostPoolFilter() {
    const hostPools = useAllHostPoolsQuery();
    const { value, setValue } = useFilterContext();

    function onChange(value: string[] | null) {
        setValue(value ? { type: ColumnFilterInputType.List, values: value } : null);
    }

    return (
        <AdminHostPoolMenuContent
            value={value.values ?? []}
            onChange={(value) => onChange(value)}
            pools={[...hostPools.values()]}
        />
    );
}
